import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../utils/api';
import { Tutorial } from '../../types/tutorial';
import { File } from '../../types/file';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface UserSearchResponse {
  success: boolean;
  data: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export const SendGiftManagement: React.FC = () => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [productType, setProductType] = useState<'Tutorial' | 'File'>('Tutorial');
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Tutorial | File | null>(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Search users with debounce
  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setUserOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingUsers(true);
      try {
        const response = await api.get<UserSearchResponse>(
          `/users?search=${encodeURIComponent(userSearchQuery)}&limit=10`
        );
        setUserOptions(response.data.data);
      } catch (error) {
        console.error('Error searching users:', error);
        setMessage({ type: 'error', text: t('admin.sendGift.errors.userSearchFailed') });
      } finally {
        setLoadingUsers(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery, t]);

  // Fetch items based on product type
  useEffect(() => {
    const fetchItems = async () => {
      setLoadingItems(true);
      setSelectedItem(null);
      try {
        if (productType === 'Tutorial') {
          const response = await api.get<{ success: boolean; data: Tutorial[] }>('/tutorials/all');
          setTutorials(response.data.data);
        } else {
          const response = await api.get<{ success: boolean; data: File[] }>('/files/all');
          setFiles(response.data.data);
        }
      } catch (error) {
        console.error(`Error fetching ${productType.toLowerCase()}s:`, error);
        setMessage({ type: 'error', text: t('admin.sendGift.errors.itemsFetchFailed') });
      } finally {
        setLoadingItems(false);
      }
    };

    fetchItems();
  }, [productType, t]);

  const handleSendGift = async () => {
    if (!selectedUser || !selectedItem) {
      setMessage({ type: 'error', text: t('admin.sendGift.errors.missingFields') });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      await api.post('/orders/gift', {
        customerId: selectedUser._id,
        productType: productType === 'Tutorial' ? 'Tutorial' : 'File',
        item: selectedItem._id,
        amount: 0
      });

      setMessage({ type: 'success', text: t('admin.sendGift.success') });
      // Reset form
      setSelectedUser(null);
      setSelectedItem(null);
      setUserSearchQuery('');
    } catch (error: any) {
      console.error('Error sending gift:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('admin.sendGift.errors.sendFailed')
      });
    } finally {
      setSending(false);
    }
  };

  const items = productType === 'Tutorial' ? tutorials : files;

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        {t('admin.sendGift.title')}
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* User Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={userOptions}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              loading={loadingUsers}
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              onInputChange={(_, newInputValue) => setUserSearchQuery(newInputValue)}
              inputValue={userSearchQuery}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('admin.sendGift.selectUser')}
                  placeholder={t('admin.sendGift.userSearchPlaceholder')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              noOptionsText={userSearchQuery.length < 2 ? t('admin.sendGift.typeToSearch') : t('admin.sendGift.noUsersFound')}
            />
          </Grid>

          {/* Product Type Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('admin.sendGift.selectType')}</InputLabel>
              <Select
                value={productType}
                label={t('admin.sendGift.selectType')}
                onChange={(e) => setProductType(e.target.value as 'Tutorial' | 'File')}
              >
                <MenuItem value="Tutorial">{t('admin.sendGift.typeTutorial')}</MenuItem>
                <MenuItem value="File">{t('admin.sendGift.typeFile')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Item Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('admin.sendGift.selectItem')}</InputLabel>
              <Select
                value={selectedItem?._id || ''}
                label={t('admin.sendGift.selectItem')}
                onChange={(e) => {
                  const item = items.find(i => i._id === e.target.value);
                  setSelectedItem(item || null);
                }}
                disabled={loadingItems || items.length === 0}
              >
                {items.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Selected Item Preview */}
          {selectedItem && (
            <Grid item xs={12}>
              <Card>
                <Box sx={{ display: 'flex' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 200, height: 200, objectFit: 'cover' }}
                    image={selectedItem.imagePath}
                    alt={selectedItem.title}
                  />
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {selectedItem.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {selectedItem.description}
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Send Gift Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSendGift}
              disabled={!selectedUser || !selectedItem || sending}
              fullWidth
            >
              {sending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('admin.sendGift.sending')}
                </>
              ) : (
                t('admin.sendGift.sendButton')
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

